import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';


const ReportViewer = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const { token, user, company, isAdmin, isAuthenticated } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [iframeLoading, setIframeLoading] = useState(true);


  // Determine file type from main_file extension
  const getFileType = (filename) => {
    if (!filename) return 'unknown';
    const ext = filename.split('.').pop().toLowerCase();
    if (['html', 'htm'].includes(ext)) return 'html';
    if (ext === 'pdf') return 'pdf';
    if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext)) return 'office';
    if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp'].includes(ext)) return 'image';
    return 'other';
  };

  useEffect(() => {
    // Wait for auth to initialize before checking
    // In a new tab, localStorage might take a moment to be read
    const checkAuth = async () => {
      // Small delay to ensure localStorage is read
      await new Promise(resolve => setTimeout(resolve, 100));
      
      if (!isAuthenticated) {
        navigate('/login');
        return;
      }
      
      if (reportId && token) {
        fetchReportDetails();
      }
    };
    
    checkAuth();
  }, [reportId, token, isAuthenticated, navigate]);

  const fetchReportDetails = async () => {
    try {
      setLoading(true);
      setError(''); // Clear any previous errors
      
      // Get report details
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/client/reports/${reportId}`;
      console.log('Fetching report from:', url);
      
      const reportRes = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      console.log('Report response status:', reportRes.status);

      if (reportRes.status === 401) {
        // Token expired or invalid, redirect to login
        console.log('Token expired, redirecting to login');
        navigate('/login');
        return;
      }

      if (reportRes.status === 404) {
        throw new Error('Reporte no encontrado. Verifique que el reporte existe.');
      }

      if (!reportRes.ok) {
        const errorData = await reportRes.json().catch(() => ({}));
        throw new Error(errorData.detail || 'No se pudo cargar el reporte');
      }

      const reportData = await reportRes.json();
      console.log('Report loaded successfully:', reportData.title);
      setReport(reportData);
    } catch (err) {
      console.error('Error loading report:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client/reports/${reportId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'No se pudo descargar el archivo');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = report.main_file.split('/').pop();
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert(err.message);
    }
  };

  // Direct view in new tab (for PDFs and files that can't be iframed)
  const handleViewInNewTab = () => {
    const viewUrl = `${process.env.REACT_APP_BACKEND_URL}/api/client/reports/${reportId}/view?token=${token}`;
    window.open(viewUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando reporte...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Error al cargar el reporte</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link
            to="/portal"
            className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 transition-colors"
          >
            Volver al Portal
          </Link>
        </div>
      </div>
    );
  }

  // Don't render iframe until we have a valid token
  if (!token) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verificando autenticación...</p>
        </div>
      </div>
    );
  }

  const fileType = getFileType(report?.main_file);
  
  // Simple file URL - report files are stored in /app/uploads/{report_id}/
  // main_file contains just the relative path within that folder (e.g., "Index.html" or "folder/Index.html")
  const iframeUrl = report?.main_file 
    ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${reportId}/${report.main_file}?token=${token}`
    : '';

  // Render different content based on file type
  const renderContent = () => {
    switch (fileType) {
      case 'html':
        // HTML files work in iframe
        return (
          <div className="relative w-full h-full" style={{ minHeight: 'calc(100vh - 120px)' }}>
            {iframeLoading && (
              <div className="absolute inset-0 bg-white flex flex-col items-center justify-center z-10">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mb-4"></div>
                <p className="text-gray-600 font-medium">Cargando reporte...</p>
                <p className="text-gray-400 text-sm mt-2">Esto puede tomar unos segundos</p>
              </div>
            )}
            <iframe
              src={iframeUrl}
              title={report?.title || 'Report'}
              className="w-full h-full border-0"
              style={{ minHeight: 'calc(100vh - 120px)' }}
              sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
              onLoad={() => setIframeLoading(false)}
            />
          </div>
        );
      
      case 'pdf':
        // PDF - use object tag for better cross-origin support, with fallback
        return (
          <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50" style={{ minHeight: 'calc(100vh - 120px)' }}>
            <object
              data={iframeUrl}
              type="application/pdf"
              className="w-full h-full"
              style={{ minHeight: 'calc(100vh - 120px)' }}
            >
              {/* Fallback for browsers that can't display embedded PDF */}
              <div className="flex flex-col items-center justify-center p-8 text-center">
                <svg className="w-24 h-24 text-red-500 mb-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 3v6a1 1 0 001 1h6" />
                </svg>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Documento PDF</h3>
                <p className="text-gray-600 mb-6">Este PDF no se puede mostrar en el navegador.</p>
                <div className="flex space-x-4">
                  <button
                    onClick={handleViewInNewTab}
                    className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors flex items-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    <span>Abrir en nueva pestaña</span>
                  </button>
                  {report?.allow_download && (
                    <button
                      onClick={handleDownload}
                      className="bg-red-600 text-white px-6 py-3 rounded-md hover:bg-red-700 transition-colors flex items-center space-x-2"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                      </svg>
                      <span>Descargar</span>
                    </button>
                  )}
                </div>
              </div>
            </object>
          </div>
        );
      
      case 'office':
        // Office documents can't be displayed - show download prompt
        return (
          <div className="w-full flex flex-col items-center justify-center bg-gray-50 p-12" style={{ minHeight: 'calc(100vh - 120px)' }}>
            <svg className="w-24 h-24 text-blue-500 mb-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-2xl font-semibold text-gray-800 mb-2">{report?.title}</h3>
            <p className="text-gray-600 mb-2">Documento de Office ({report?.main_file?.split('.').pop()?.toUpperCase()})</p>
            <p className="text-gray-500 text-sm mb-8">Los documentos de Office no se pueden previsualizar en el navegador.</p>
            <div className="flex space-x-4">
              {report?.allow_download ? (
                <button
                  onClick={handleDownload}
                  className="bg-red-600 text-white px-8 py-3 rounded-md hover:bg-red-700 transition-colors flex items-center space-x-2 text-lg"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                  </svg>
                  <span>Descargar documento</span>
                </button>
              ) : (
                <p className="text-orange-600 bg-orange-50 px-6 py-3 rounded-md">
                  La descarga no está habilitada para este documento.
                </p>
              )}
            </div>
          </div>
        );
      
      case 'image':
        // Images can be displayed directly
        return (
          <div className="w-full flex items-center justify-center bg-gray-900 p-4" style={{ minHeight: 'calc(100vh - 120px)' }}>
            <img
              src={iframeUrl}
              alt={report?.title || 'Image'}
              className="max-w-full max-h-full object-contain"
              style={{ maxHeight: 'calc(100vh - 150px)' }}
            />
          </div>
        );
      
      default:
        // Unknown file type - offer download
        return (
          <div className="w-full flex flex-col items-center justify-center bg-gray-50 p-12" style={{ minHeight: 'calc(100vh - 120px)' }}>
            <svg className="w-24 h-24 text-gray-400 mb-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">{report?.title}</h3>
            <p className="text-gray-600 mb-6">Este tipo de archivo no se puede previsualizar.</p>
            <div className="flex space-x-4">
              <button
                onClick={handleViewInNewTab}
                className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors"
              >
                Abrir en nueva pestaña
              </button>
              {report?.allow_download && (
                <button
                  onClick={handleDownload}
                  className="bg-red-600 text-white px-6 py-3 rounded-md hover:bg-red-700 transition-colors"
                >
                  Descargar
                </button>
              )}
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3">
            <div className="flex items-center space-x-4">
              <Link to="/portal" className="text-gray-600 hover:text-gray-900">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </Link>
              <div>
                <h1 className="text-lg font-semibold text-gray-900 truncate max-w-md">
                  {report?.title}
                </h1>
                <p className="text-sm text-gray-500">{company?.name}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {report?.allow_download && (
                <button
                  onClick={handleDownload}
                  className="bg-red-600 text-white px-4 py-2 rounded-md text-sm hover:bg-red-700 transition-colors flex items-center space-x-2"
                  data-testid="download-button"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                  </svg>
                  <span>Descargar</span>
                </button>
              )}
              
              {isAdmin && (
                <Link
                  to="/admin"
                  className="bg-gray-600 text-white px-4 py-2 rounded-md text-sm hover:bg-gray-700 transition-colors"
                >
                  Admin
                </Link>
              )}
              
              <Link
                to="/portal"
                className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm hover:bg-gray-300 transition-colors"
              >
                Cerrar
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Report Content - different rendering based on file type */}
      <div className="flex-1 bg-white">
        {renderContent()}
      </div>

      {/* Footer */}
      <div className="bg-gray-800 text-gray-400 text-center py-4 text-sm">
        <p>© 2024 InsightPlace. Documento confidencial - No compartir.</p>
      </div>
    </div>
  );
};

export default ReportViewer;
